import React, { useState, useEffect } from 'react';

// Efecto de cascada para el texto del bot
const TypewriterText = ({ text }) => {
  const [currentText, setCurrentText] = useState('');

  useEffect(() => {
    // Resetear el texto cada vez que cambie el mensaje
    setCurrentText(''); 
    
    // Agregamos un delay de 100ms para evitar que la cascada se coma letras
    const timeout = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        // Usamos la versión de función de setState para mayor precisión
        setCurrentText((prev) => text.slice(0, i + 1));
        i++;
        if (i >= text.length) clearInterval(interval);
      }, 30);
      
      return () => clearInterval(interval);
    }, 100); 

    return () => clearTimeout(timeout);
  }, [text]);

  return <span style={{ whiteSpace: 'pre-wrap' }}>{currentText}</span>;
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  
  const [messages, setMessages] = useState([
    { 
      role: 'bot', 
      text: 'Hola, soy tu Asistente Digital PABS. Protegemos a tu familia en todo México y EE.UU. Contamos con planes desde $125 semanales. ¿Te gustaría conocer nuestros pagos quincenales o mensuales?' 
    }
  ]);

  // FUNCIÓN WEBHOOK: Envía los datos directamente a tu escenario de Make
  const sendToWebhook = async (userData) => {
    try {
      await fetch('https://hook.us2.make.com/161y3hlgy73o70uueinbgxqen2v7a45h', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agencia: "Tecolotl Tech Labs",
          proyecto: "PABS Landing",
          lead_nombre: "Prospecto Web",
          fecha: new Date().toLocaleString(),
          ...userData
        }),
      });
      console.log("Datos enviados a Make correctamente");
    } catch (err) {
      console.error("Error al contactar el Webhook:", err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMsg = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    setInput('');

    // 1. DETECCIÓN DE TELÉFONO: Si el usuario escribe 10 dígitos seguidos
    const phoneRegex = /\b\d{10}\b/;
    if (phoneRegex.test(currentInput)) {
      // Enviamos el dato al Webhook de inmediato
      sendToWebhook({ 
        telefono: currentInput, 
        tipo_evento: "Captura de Teléfono",
        mensaje_original: currentInput 
      });

      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'bot', 
          text: 'Perfecto, he recibido tu número. Nuestro asesor Gustavo Márquez Ríos se pondrá en contacto contigo a la brevedad para darte todos los detalles.' 
        }]);
      }, 600);
      return;
    }

    // 2. INTERCEPCIÓN: Solicitud directa de asesor
    if (currentInput.toLowerCase().includes('asesor') || currentInput.toLowerCase().includes('hablar con alguien')) {
      sendToWebhook({ intencion: "Solicitud de Asesor Humano", mensaje: currentInput });
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          role: 'bot', 
          text: '¡Claro! Puedes contactar directamente a Gustavo Márquez Ríos al 8719593901 para una atención personalizada inmediata sobre nuestros planes.' 
        }]);
      }, 500);
      return;
    }

    // 3. RESPUESTA DE IA (Tu backend en el puerto 5000)
    try {
      const response = await fetch("https://pabs-backend-changed.onrender.com/api/chat", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: currentInput,
          context: "Planes: Elite C ($23,900), Regazo ($26,900), Italia ($32,000). Cobertura México/USA. Pagos desde $125 semanales. Si el usuario acepta info, PIDE SU TELÉFONO."
        }),
      });
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.response }]);
    } catch (error) {
      // Fallback si el servidor está apagado
      setMessages(prev => [...prev, { 
        role: 'bot', 
        text: 'Por ahora tengo dificultades técnicas, pero no te preocupes. Llama a Gustavo Márquez al 8719593901 y él te atenderá personalmente.' 
      }]);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen ? (
        <div className="bg-zinc-900 border border-zinc-800 w-85 h-[500px] rounded-3xl flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
          
          {/* Header */}
          <div className="bg-yellow-400 p-5 text-black flex justify-between items-center shadow-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full border-2 border-black/20 overflow-hidden bg-white shadow-inner">
                <img src="/logo-pabs.png" alt="Bot" className="h-full w-full object-contain p-1" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">Asesoría 24/7</span>
                <span className="text-sm font-black uppercase tracking-tighter">Protección PABS</span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-2xl font-light hover:rotate-90 transition-transform">&times;</button>
          </div>

          {/* Chat Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-zinc-950/30">
            {messages.map((m, i) => (
              <div key={i} className={`${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block p-4 rounded-[1.5rem] shadow-md max-w-[85%] ${
                  m.role === 'user' 
                    ? 'bg-yellow-400 text-black font-bold rounded-tr-none' 
                    : 'bg-zinc-800 text-zinc-100 rounded-tl-none border border-zinc-700/50'
                }`}>
                  <p className="text-[13px] font-bold leading-relaxed tracking-tight">
                    {m.role === 'bot' ? <TypewriterText text={m.text} /> : m.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="p-4 bg-zinc-900 border-t border-zinc-800">
            <div className="flex gap-2 mb-3 overflow-x-auto pb-2 no-scrollbar">
              {['Precios', 'Cobertura USA', 'Asesor'].map(tag => (
                <button 
                  key={tag}
                  onClick={() => setInput(tag)}
                  className="whitespace-nowrap bg-zinc-800 border border-zinc-700 px-3 py-1 rounded-full text-[10px] font-bold text-zinc-400 hover:text-yellow-400 transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1 bg-black p-3 rounded-2xl outline-none text-white text-xs border border-zinc-800 focus:border-yellow-400/50 transition-all font-semibold" 
                placeholder="Escribe tu duda o número de teléfono..." 
              />
              <button 
                onClick={sendMessage} 
                className="bg-yellow-400 p-3 rounded-2xl text-black hover:bg-yellow-500 transition-all shadow-lg"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all border-4 border-black group"
        >
          <img src="/logo-pabs.png" alt="PABS" className="w-8 h-8 rounded-full bg-white p-0.5 group-hover:rotate-12 transition-transform" />
        </button>
      )}
    </div>
  );
};

export default Chatbot;